import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import EventBusyIcon from '@mui/icons-material/EventBusy';

function ConfirmRemoveScheduleDialog({
  open,
  handleClose,
  onConfirm,
  onRemoveAll,
  loading,
  hasActiveBooking,
  schedule,
  hasMultiple,
}) {
  const { t } = useTranslation();

  const formatScheduleTime = (sched) => {
    if (!sched?.start_time) return '';
    const start = new Date(sched.start_time);
    const end = sched.end_time ? new Date(sched.end_time) : null;
    const startStr = start.toLocaleDateString('fi-FI', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
    if (end) {
      const endStr = end.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
      return `${startStr} - ${endStr}`;
    }
    return startStr;
  };

  // "Remove all" mode: hasMultiple && no specific schedule selected
  const isRemoveAllMode = hasMultiple && !schedule;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 1 }}>
        <Box sx={{ mb: 2 }}>
          <EventBusyIcon sx={{ fontSize: 48, color: 'error.main' }} />
        </Box>
        {isRemoveAllMode ? t('Remove all schedules?') : t('Remove schedule?')}
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
        {isRemoveAllMode ? (
          <DialogContentText>
            {t('This will remove all scheduled times for this maintenance.')}
          </DialogContentText>
        ) : (
          <DialogContentText>
            {schedule && hasMultiple
              ? `${t('Remove this time slot?')} ${formatScheduleTime(schedule)}`
              : t('This will remove the scheduled time for this maintenance. You can reschedule it later.')}
          </DialogContentText>
        )}
        {hasActiveBooking && (
          <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
            {t('booking_linked_remove_warning')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, gap: 1, justifyContent: 'center' }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            borderRadius: '10px',
            px: 3,
            py: 1,
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'text.primary',
              bgcolor: 'transparent',
            },
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          onClick={isRemoveAllMode ? onRemoveAll : onConfirm}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            borderRadius: '10px',
            px: 3,
            py: 1,
          }}
        >
          {t('Yes, remove')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmRemoveScheduleDialog;
